import { Link } from 'react-router-dom'
import { CardContainer, ChartContainer, ProgressBar } from './styles'
import { Chart as ChartJS, ArcElement } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import ActionsModal from '../ActionsModal'
import { useImmer } from 'use-immer'
import RenameModal from '../RenameModal'
import CopyModal from '../CopyModal'
import DeleteModal from '../DeleteModal'
import TooltipWrapper from '../TooltipWrapper'
import { toast } from 'react-toastify'
import 'katex/dist/katex.min.css'
import { InlineMath } from 'react-katex'
import ReactDOM from 'react-dom'
import { useReactToPrint } from 'react-to-print'
import PrintPDF from '../PrintPDF'
import { TQuestionBook } from 'pages/Questions/components/QuestionsContent/types'
import { RootState } from 'store/modules/rootReducer'
import TaskList from 'assets/icons/task-list'
import Trash from 'assets/icons/trash'
import Copy from 'assets/icons/copy'
import Print from 'assets/icons/print'
import { GET_QUESTION_BOOK_ITEMS } from 'pages/MyQuestionBooks/services'

ChartJS.register(ArcElement)

interface IProps {
  data: TQuestionBook
}

type TLabel = 'correct' | 'wrong' | 'left' | 'correct-percentage'

const Card = (props: IProps) => {
  const { isDarkMode } = useSelector((state: RootState) => state.theme)

  const [label, setLabel] = useState<TLabel>('correct-percentage')
  const [renderPrintQuestionBook, setRenderPrintQuestionBook] = useState<boolean>(false)
  const [allQuestionBookItems, setAllQuestionBookItems] = useState([])
  const [loadingPrintPDF, setLoadingPrintPDF] = useState<boolean>(false)

  const [modals, setModals] = useImmer({
    actions: false,
    rename: false,
    copy: false,
    delete: false,
  })

  const { hit, wrong } = props.data.statistics

  const isCompleted = hit.number + wrong.number === props.data.totalQuestions
  const hasStarted = Boolean(hit.number) || Boolean(wrong.number)
  const progress = (() => {
    const value = ((hit.number + wrong.number) / props.data.totalQuestions) * 100

    return value > 1 ? Math.round(value) : Math.round(value * 10) / 10
  })()

  const openModal = (modal: keyof typeof modals) => () =>
    setModals((draft) => {
      draft[modal] = true
    })

  const closeModal = (modal: keyof typeof modals) => () =>
    setModals((draft) => {
      draft[modal] = false
    })

  const renderSpecialCharacters = useCallback(() => {
    const optionsTags = document.getElementsByClassName(`questionOption-${props.data.code}`)
    const statementTag = document.getElementById(`question-option-${props.data.code}`)

    const elements = [...Array.from(optionsTags), statementTag].filter(Boolean)

    if (!elements.length) return

    elements.forEach((el) => {
      const htmlContent = el.innerHTML
      el.innerHTML = htmlContent.replace(/\\[n|r|t]/g, '<br>')
    })
  }, [props.data.code])

  const renderMathForm = useCallback(() => {
    const tags = document.getElementsByTagName('math-component')
    if (tags && tags.length) {
      for (let i = 0; i < tags.length; i++) {
        const newEl = tags[i].insertAdjacentElement('afterend', document.createElement('div'))
        newEl.id = `math${i}`
        newEl.setAttribute('style', tags[i].getAttribute('style') || '')

        const mathElem = tags[i] as HTMLDivElement

        mathElem.style.display = 'none'

        ReactDOM.render(<InlineMath math={tags[i].innerHTML} />, newEl, () => {
          if (i === tags.length - 1) {
            renderSpecialCharacters()
          }
        })
      }
    }
  }, [renderSpecialCharacters])

  const printQuestionBook = async () => {
    try {
      if (loadingPrintPDF) {
        toast.info('Aguarde o caderno ser gerado!')
        return
      }

      setLoadingPrintPDF(true)
      toast.info('Caderno sendo preparado para download, aguarde!')
      const response = await GET_QUESTION_BOOK_ITEMS(props.data._id)

      const result = response?.map((q) => {
        const title = q.title
        const firstTag = title.indexOf('>')

        const createQuesionWithQuestionNumber =
          title.substring(0, firstTag + 1) +
          `<b>${q.questionNumber} - </b>` +
          title.substring(firstTag + 1)

        q.title = createQuesionWithQuestionNumber

        return q
      })

      setAllQuestionBookItems(result)
      setRenderPrintQuestionBook(true)
      renderMathForm()
      handlePrint()
    } catch (error) {
      console.log(error)
      toast.error('Falha ao preparar caderno para download, tente novamente')
    }
  }

  const ref = useRef<HTMLDivElement>()

  const questionBookPrintRef = useRef<HTMLDivElement>()

  const handlePrint = useReactToPrint({
    content: () => questionBookPrintRef.current,
    onAfterPrint: () => {
      setRenderPrintQuestionBook(false)
      setLoadingPrintPDF(false)
    },
    onBeforePrint: () => renderMathForm(),
    pageStyle: 'margin: 20 !important; display: flex !important',
    documentTitle: allQuestionBookItems[0]?.questionBookName,
    removeAfterPrint: false,
  })

  return (
    <>
      <CardContainer ref={ref}>
        <div className="start">
          <ChartContainer>
            <div className="label" data-visible={label === 'correct'}>
              <p className="value">{hit.number}</p>
              <p className="text">acertos</p>
            </div>
            <div className="label" data-visible={label === 'wrong'}>
              <p className="value">{wrong.number}</p>
              <p className="text">erros</p>
            </div>
            <div className="label" data-visible={label === 'left'}>
              <p className="value">{props.data.totalQuestions - (hit.number + wrong.number)}</p>
              <p className="text">restantes</p>
            </div>
            <div className="label" data-visible={label === 'correct-percentage'}>
              <p className="value">{Math.round(hit.rate) + '%'}</p>
              <p className="text">{hasStarted ? 'acertos' : '-'}</p>
            </div>

            <Doughnut
              options={{
                onHover(event, activeElement) {
                  const labels = {
                    0: 'correct',
                    1: 'wrong',
                  }

                  setLabel(labels[activeElement[0]?.index] || 'left')
                },
                cutout: 28,
              }}
              onMouseLeave={() => setTimeout(() => setLabel('correct-percentage'), 20)}
              className="chart"
              data={{
                datasets: [
                  {
                    data: [
                      props.data.statistics.hit.rate,
                      props.data.statistics.wrong.rate,
                      hasStarted ? 0 : 100,
                    ],
                    backgroundColor: [
                      isDarkMode ? 'rgb(0, 183, 0)' : 'rgb(0, 159, 0)',
                      isDarkMode ? 'rgb(235, 109, 96)' : 'rgb(231, 76, 60)',
                      isDarkMode ? 'rgb(74, 74, 74)' : 'rgb(221, 221, 221)',
                    ],
                    borderColor: isDarkMode ? 'rgb(13, 13, 13)' : 'rgb(255,  255, 255)',
                    hoverBorderColor: isDarkMode ? 'rgb(13, 13, 13)' : 'rgb(255,  255, 255)',
                    borderWidth: 4,
                    hoverBackgroundColor: [
                      isDarkMode ? 'rgb(0, 183, 0, 0.75)' : '#008700',
                      isDarkMode ? 'rgb(235, 109, 96, 0.75)' : '#dc2d1b',
                      isDarkMode ? 'rgb(74, 74, 74, 0.75)' : '#bcbcbc',
                    ],
                  },
                ],
              }}
            />
          </ChartContainer>
          <div>
            <p className="name">{props.data.name}</p>

            <div className="infos">
              <span className="info">
                {format(props.data.totalPublicTenders)}{' '}
                {props.data.totalPublicTenders === 1 ? 'órgão' : 'órgãos'} •{' '}
              </span>{' '}
              <span className="info">
                {format(props.data.totalSubjects)}{' '}
                {props.data.totalSubjects === 1 ? 'disciplina' : 'disciplinas'} •{' '}
              </span>{' '}
              <span className="info">
                {format(props.data.totalThemes)}{' '}
                {props.data.totalThemes === 1 ? 'assunto' : 'assuntos'} •{' '}
              </span>{' '}
              <span className="info">
                {format(props.data.totalQuestions)}{' '}
                {props.data.totalQuestions === 1 ? 'questão' : 'questões'}
              </span>
            </div>

            <ProgressBar progress={progress}>
              <div className="text" data-no-progress={progress === 0}>
                {progress}% concluído
              </div>
              <div className="colored-bar">{progress}% concluído</div>
            </ProgressBar>
          </div>
        </div>

        <div className="end">
          <button className="open-action-modal" onClick={openModal('actions')}>
            Ações
          </button>

          <Link className="link" to={`/questoes/caderno/${props.data._id}`}>
            {isCompleted ? 'Ver detalhes' : 'Responder'}
          </Link>

          <div className="actions-buttons">
            <div className="group">
              <TooltipWrapper text="Renomear caderno">
                <button onClick={openModal('rename')}>
                  <TaskList />
                </button>
              </TooltipWrapper>
              <TooltipWrapper text="Copiar caderno">
                <button onClick={openModal('copy')}>
                  <Copy />
                </button>
              </TooltipWrapper>
            </div>
            <div className="group">
              <TooltipWrapper text="Imprimir/Gerar PDF">
                <button disabled={loadingPrintPDF} onClick={() => printQuestionBook()}>
                  <Print />
                </button>
              </TooltipWrapper>
              <TooltipWrapper text="Excluir caderno">
                <button onClick={openModal('delete')}>
                  <Trash />
                </button>
              </TooltipWrapper>
            </div>
          </div>
        </div>
      </CardContainer>

      <ActionsModal
        isOpen={modals.actions}
        questionBook={props.data}
        onClose={closeModal('actions')}
        onCopyClick={openModal('copy')}
        onRenameClick={openModal('rename')}
        onDeleteClick={openModal('delete')}
        onPrintClick={() => printQuestionBook()}
        disabledPrintPdf={loadingPrintPDF}
      />
      <RenameModal
        isOpen={modals.rename}
        onClose={closeModal('rename')}
        questionBook={props.data}
      />
      <CopyModal isOpen={modals.copy} onClose={closeModal('copy')} questionBook={props.data} />
      <DeleteModal
        isOpen={modals.delete}
        onClose={closeModal('delete')}
        questionBook={props.data}
      />
      {renderPrintQuestionBook && (
        <PrintPDF
          questionBookPrintRef={questionBookPrintRef}
          allQuestionBookItems={allQuestionBookItems}
        />
      )}
    </>
  )
}

const format = (num: number) => new Intl.NumberFormat('pt-BR').format(num)

export default Card
